import { familiesInPlay, FAMILIES } from "@/content/families"
import type { CardId } from "@/types/card"

/*
 * Card ids and card faces.
 *
 * A card id (`f<family>c<index>`) is the STABLE identifier a pick, a placement
 * and a deep-dive answer all store; the face and the printed number are lookups
 * for display only. The six-family re-cut therefore changes text, numbering and
 * which cards are active WITHOUT touching an id: a survivor keeps its id (so old
 * sessions resolve unchanged) and a dropped card is simply absent from its
 * family's `cardIds` while its face stays here.
 */

/** `f3c2` — family 3, card 2. See `types/card` for why the format is this. */
export function cardId(family: number, index: number): CardId {
  return `f${family}c${index}`
}

/**
 * How the deck prints it: "5.1". Display only, never stored.
 *
 * Decoupled from the id because the re-cut renumbered the print face without
 * moving any id: `f1c5` now prints 1.3, and the whole Reflects/Partners families
 * (ids `f6*`/`f7*`) print as 5 and 6. A retired id — one off the current deck —
 * is not in the map and falls back to its own `f.c` number; that historical
 * number can collide with a survivor's new one, but only ever inside an old
 * session's report, and the card is still named by its statement.
 */
const CARD_NUMBERS: Record<CardId, string> = {
  f1c1: "1.1",
  f1c2: "1.2",
  f1c5: "1.3",
  f1c3: "1.4",
  f2c1: "2.1",
  f2c2: "2.2",
  f2c3: "2.3",
  f2c4: "2.4",
  f3c1: "3.1",
  f3c2: "3.2",
  f3c3: "3.3",
  f3c4: "3.4",
  f4c1: "4.1",
  f4c5: "4.2",
  f4c3: "4.3",
  f4c4: "4.4",
  f6c1: "5.1",
  f6c2: "5.2",
  f6c3: "5.3",
  f6c4: "5.4",
  f7c1: "6.1",
  f7c2: "6.2",
  f7c3: "6.3",
  f7c4: "6.4",
}

export function cardNumber(id: CardId): string {
  const printed = CARD_NUMBERS[id]
  if (printed) return printed
  const parsed = parseCardId(id)
  return parsed ? `${parsed.family}.${parsed.index}` : id
}

export function parseCardId(id: CardId): { family: number; index: number } | null {
  const match = /^f(\d+)c(\d+)$/.exec(id)
  if (!match) return null
  return { family: Number(match[1]), index: Number(match[2]) }
}

/** The family's active card ids, in printed order — straight off the family
 *  table, which is where the re-cut's gaps and reorderings live. */
export function cardIdsForFamily(family: number): CardId[] {
  const found = FAMILIES.find((entry) => entry.n === family)
  return found ? [...found.cardIds] : []
}

/**
 * Every card the chosen families put in play, in DEAL order.
 *
 * A family the session does not play contributes nothing, which is what makes
 * `config.families` meaningful: a card outside it can never be picked.
 *
 * Deal order rather than numbering — see `FAMILY_DEAL_ORDER`. This is the array
 * the deal walks, so it decides which card the room is shown first.
 */
export function cardsInPlay(families: number[]): CardId[] {
  return familiesInPlay(families).flatMap((family) => cardIdsForFamily(family.n))
}

/** Whether a stored id still names a card the session plays. */
export function cardIsInPlay(id: CardId, families: number[]): boolean {
  const parsed = parseCardId(id)
  if (!parsed) return false
  if (!families.includes(parsed.family)) return false
  return cardIdsForFamily(parsed.family).includes(id)
}

/**
 * What is printed on a card.
 *
 * `statement` is the line set large on the face — it is what the room reads and
 * argues about, so it is the label everywhere a card is named. `name` is the
 * short handle, and rides above the statement as an eyebrow. The other four are
 * the back of the card: three the room reads while choosing, and `scope`, which
 * is the one the leave-behind prints under every card that was kept.
 */
export interface CardFace {
  name: string
  statement: string
  looksLike: string
  needs: string
  watchOut: string
  scope: string
}

/** The hover affordance that opens a thumbnail at readable size. */
export const CARD_PEEK_COPY = {
  open: (name: string) => `Preview ${name}`,
} as const

/** The back-of-card labels, in the order they are read. */
export const CARD_BACK_LABELS = {
  looksLike: "Looks like",
  needs: "Needs",
  watchOut: "Watch out",
  scope: "We would scope",
} as const

export const CARD_FACES: Record<CardId, CardFace> = {
  f1c1: {
    name: "See & Recognize",
    statement: "Detect objects, people, scenes, or environmental changes.",
    looksLike:
      "A camera or an upload that names what's in the frame — a damaged part, an empty shelf, someone at the door.",
    needs:
      "Labelled images from your own environment, not stock ones. A few hundred real ones beat a perfect ten.",
    watchOut: "Lighting, angles and edge cases break vision models faster than anything else.",
    scope:
      "Vision model over one narrow object set, with a confidence threshold and a human check below it.",
  },
  f1c2: {
    name: "Hear & Understand",
    statement: "Recognize speech, environmental sounds, or audio patterns.",
    looksLike:
      "Calls, site audio or voice notes turned into searchable text with the moments that matter flagged.",
    needs: "Audio that is clean enough, and a glossary of your jargon, product names and acronyms.",
    watchOut: "Accents, crosstalk and background noise wreck accuracy that demos beautifully.",
    scope: "Transcription pipeline for one audio source, plus a searchable index of key moments.",
  },
  f1c3: {
    name: "Read & Interpret",
    statement: "Extract meaning from text, documents, or structured data.",
    looksLike: "A PDF, invoice or contract goes in and structured fields come out the other side.",
    needs: "A representative sample of the real, messy documents — including the bad scans.",
    watchOut: "One layout change upstream and the extraction quietly starts lying.",
    scope:
      "Extraction for one document type into a defined schema, with field-level confidence shown.",
  },
  f1c4: {
    name: "Sense the Environment",
    statement: "Identify patterns in IoT devices, sensors, or geolocation.",
    looksLike: "Sensor or location data becomes a readable state — “line 3 is running hot”.",
    needs: "A reliable feed with timestamps you trust, and an agreed definition of normal.",
    watchOut: "Missing data looks exactly like “nothing is wrong”.",
    scope:
      "Ingest one sensor stream, derive two or three states, surface them where decisions happen.",
  },
  f1c5: {
    name: "Combine Multiple Inputs",
    statement: "Integrate visual, audio, and text data for richer context.",
    looksLike:
      "A photo, a note and a reading are read together to explain something none of them could alone.",
    needs: "Inputs already joined by a shared key — a job, a case, an asset.",
    watchOut: "Fusion multiplies the cost of every weak input sitting underneath it.",
    scope:
      "Join two existing inputs on one workflow and prove the combined read beats each on its own.",
  },
  f2c1: {
    name: "Explain & Generate Text",
    statement: "Summarize, revise, or generate insights.",
    looksLike:
      "A long thread, ticket or report collapses into the three lines someone will act on.",
    needs: "Examples of the good version, written by whoever does this by hand today.",
    watchOut: "Fluent summaries hide omissions, and people stop opening the source.",
    scope: "Draft generation for one artefact, with the source always one click away.",
  },
  f2c2: {
    name: "Speak Naturally",
    statement: "Convert text to lifelike voice output.",
    looksLike: "Updates, instructions or alerts read aloud when hands and eyes are busy.",
    needs: "A voice that fits the brand, and pronunciation rules for your terms and names.",
    watchOut: "Synthetic warmth in a serious moment reads as insincere.",
    scope: "Text-to-speech on one notification path, with an obvious way to silence it.",
  },
  f2c3: {
    name: "Converse in Context",
    statement: "Maintain dialogue, remember interactions, and answer questions.",
    looksLike:
      "An assistant that remembers what happened upstream and doesn't make people start again.",
    needs:
      "Session and account context available at answer time, plus a defined scope for saying no.",
    watchOut: "Memory without limits becomes a privacy surface nobody designed.",
    scope: "Scoped assistant over a fixed knowledge set, with refusal and human handoff paths.",
  },
  f2c4: {
    name: "Translate & Adapt Style",
    statement: "Switch languages, adjust tone, and adapt messages.",
    looksLike: "The same message, correct in another language and pitched for a different reader.",
    needs: "A tone guide and a glossary that somebody actually owns.",
    watchOut: "Regulated or legal copy is not a translation problem.",
    scope: "Translation for one content type into two languages, with named human sign-off.",
  },
  f2c5: {
    name: "Personalize Communication",
    statement: "Tailor experiences to individual preferences.",
    looksLike: "What each person sees first shifts with their role and what they did last.",
    needs: "Behaviour data, and a clear rule for what is allowed to drive personalisation.",
    watchOut: "Personalisation people can't see or switch off feels like surveillance.",
    scope: "Two or three visible rules on one surface, each with a “why am I seeing this”.",
  },
  f3c1: {
    name: "Retrieve Knowledge Quickly",
    statement: "Find relevant information.",
    looksLike: "An answer with its source attached, instead of a folder to go and dig through.",
    needs: "Content that is findable, current, and owned by a named person.",
    watchOut: "Retrieval over stale content industrialises the wrong answer.",
    scope: "Search and answer over one curated corpus, citations shown every time.",
  },
  f3c2: {
    name: "Plan & Solve Problems",
    statement: "Suggest solutions or action sequences.",
    looksLike: "A goal turns into a sequence a person can edit before anything runs.",
    needs: "The constraints written down — capacity, cost, rules, order of operations.",
    watchOut: "A plausible plan that breaks an unwritten rule destroys trust in one demo.",
    scope: "Plan generation for one recurring task, always reviewed before execution.",
  },
  f3c3: {
    name: "Predict Outcomes",
    statement: "Forecast trends, risks, or potential results.",
    looksLike: "A likelihood on the thing people currently guess at — churn, delay, demand.",
    needs: "History with outcomes attached, and enough of it to be worth trusting.",
    watchOut: "A prediction nobody can act on is a number, not a feature.",
    scope: "One prediction on one decision, measured against the guess it replaces.",
  },
  f3c4: {
    name: "Generate Creative Ideas",
    statement: "Propose new concepts or strategies.",
    looksLike: "Ten starting points in the time it used to take to write one.",
    needs: "A real brief, and someone with the taste to kill nine of them.",
    watchOut: "Volume without judgement moves the work downstream, it doesn't remove it.",
    scope: "Idea generation inside one existing creative step, human curation kept in place.",
  },
  f3c5: {
    name: "Remember Context",
    statement: "Maintain interaction history for better reasoning.",
    looksLike: "The product picks up where the person left off, across sessions and channels.",
    needs: "Somewhere to store context, and a retention rule you can defend.",
    watchOut: "Remembering the wrong thing is worse than remembering nothing.",
    scope: "Persistent context on one journey, visible to the user and clearable by them.",
  },
  f4c1: {
    name: "Trigger Actions",
    statement: "Initiate actions across systems and services.",
    looksLike: "A signal fires the next step without anyone sitting there watching for it.",
    needs: "An event you trust, and an explicit definition of when not to fire.",
    watchOut: "Every trigger needs an undo before it needs a feature flag.",
    scope: "One trigger, one action, reversible, with a complete log.",
  },
  f4c2: {
    name: "Execute Tasks",
    statement: "Execute actions automatically.",
    looksLike:
      "The routine step just happens — the form filled, the record updated, the file filed.",
    needs: "Permissions, and a written boundary for what it may never touch.",
    watchOut: "Silent execution is only acceptable for as long as it's right.",
    scope: "Automate one low-blast-radius task, with confirmation on anything costly.",
  },
  f4c3: {
    name: "Automate Workflows",
    statement: "Orchestrate multi-step workflows.",
    looksLike: "A multi-step process runs end to end, and stops cleanly when it can't.",
    needs: "The current process mapped, including the exceptions people handle informally.",
    watchOut: "Automating an unexamined process just makes the mess faster.",
    scope: "One workflow, happy path automated, exceptions routed to a named person.",
  },
  f4c4: {
    name: "Integrate Systems & APIs",
    statement: "Interact with external systems.",
    looksLike: "The work stops bouncing between tabs, tools and copy-paste.",
    needs: "APIs that exist, and owners who will grant access this quarter.",
    watchOut:
      "Integration effort is nearly always underestimated, usually in the systems you don't own.",
    scope: "One integration, one direction, with retries and an alert when it fails.",
  },
  f4c5: {
    name: "Monitor & Adjust Actions",
    statement: "Observe execution and adapt.",
    looksLike: "The system notices its own action didn't land, then corrects or escalates.",
    needs: "A definition of success for each action that can be checked afterwards.",
    watchOut: "Self-correction without a ceiling turns one error into a loop.",
    scope: "Post-action verification on one automated path, escalating to a human after two tries.",
  },
  f5c1: {
    name: "Detect Anomalies",
    statement: "Spot irregularities or unexpected events.",
    looksLike: "The odd one out gets surfaced before anyone thought to go looking for it.",
    needs: "Enough normal history to know what normal actually is.",
    watchOut: "A high false-positive rate trains people to ignore the product.",
    scope: "Anomaly detection on one metric, tuned to a volume people will actually read.",
  },
  f5c2: {
    name: "Alert to Change",
    statement: "Notify users about important shifts or errors.",
    looksLike: "The right person is told, in the place they already look.",
    needs: "Routing rules — who cares, how urgently, and where they’ll see it.",
    watchOut: "Alerts without a next step become noise within a fortnight.",
    scope: "One alert type, one channel, each alert carrying its recommended action.",
  },
  f5c3: {
    name: "Monitor Context",
    statement: "Continuously track signals and thresholds.",
    looksLike: "A live read on conditions, so decisions stop starting with “let me go and check”.",
    needs: "A stream that stays up, and thresholds with an owner's name against them.",
    watchOut: "Continuous monitoring carries continuous cost, in money and in attention.",
    scope: "One monitored view, two or three thresholds, and a named on-call owner.",
  },
  f5c4: {
    name: "Forecast Warnings",
    statement: "Predict and flag possible failures or bottlenecks.",
    looksLike: "A heads-up early enough to change the outcome, not just explain it afterwards.",
    needs: "Lead time. A warning that lands too late is a report.",
    watchOut: "Early warnings with weak precision get switched off after the first false alarm.",
    scope: "One forecast warning with a stated lead time and an agreed accuracy target.",
  },
  f6c1: {
    name: "Explainability",
    statement: "Describe how and why a result was generated.",
    looksLike: "The answer arrives with what drove it, in language the user actually speaks.",
    needs: "Access to the features or sources behind each output.",
    watchOut: "An explanation nobody can check is decoration.",
    scope: "Per-output reasoning on one screen, checkable against the underlying source.",
  },
  f6c2: {
    name: "Guidance & Coaching",
    statement: "Suggest next steps or improvements.",
    looksLike: "The product suggests the better next move, at the moment it matters.",
    needs: "A model of what good looks like, agreed with the people being coached.",
    watchOut: "Coaching that reads as scoring makes people work around the tool.",
    scope: "Contextual suggestions on one task, dismissible and never blocking.",
  },
  f6c3: {
    name: "Transparency",
    statement: "Search for information to help validate decisions.",
    looksLike: "The user can see where information came from and how fresh it is.",
    needs: "Provenance carried through the pipeline, not reconstructed at the end.",
    watchOut: "Partial transparency is usually read as a cover-up.",
    scope: "Source, date and confidence shown wherever generated content appears.",
  },
  f6c4: {
    name: "Critical Thinking Support",
    statement: "Challenge assumptions and highlight blind spots.",
    looksLike: "The product asks the question that was missing from the room.",
    needs: "A clear stance on when it should push back, agreed with the business.",
    watchOut: "Challenge without warmth reads as obstruction and gets muted.",
    scope: "Assumption prompts at one decision point, framed as questions rather than verdicts.",
  },
  f7c1: {
    name: "Co-Creation Tools",
    statement: "Brainstorm or design alongside users.",
    looksLike: "Person and system work on the same artefact, and both can edit it.",
    needs: "A shared object with version history — otherwise it's just chat.",
    watchOut: "Co-creation without clear authorship creates review deadlock.",
    scope: "One shared artefact: AI drafts, humans edit, full change history kept.",
  },
  f7c2: {
    name: "Decision Support",
    statement: "Visualize data for human interpretation.",
    looksLike: "The trade-off is laid out so a person can own the call.",
    needs: "Agreement on which options and which criteria are on the table.",
    watchOut: "A recommendation dressed up as options quietly removes the choice.",
    scope: "One decision view: the options, the criteria, and what each choice costs.",
  },
  f7c3: {
    name: "Adaptive Assistance",
    statement:
      "Abstract technical complexity, allowing users to perform tasks using natural language instead of coding.",
    looksLike: "People ask for what they want in their own words instead of learning the tool.",
    needs: "A bounded set of things it can genuinely do, and honesty about the edges.",
    watchOut: "A natural-language front door promises everything behind it.",
    scope: "Plain-language entry to a fixed set of actions, with visible limits.",
  },
  f7c4: {
    name: "Ethical Awareness",
    statement: "Help users understand trade-offs and risks.",
    looksLike: "Risks, trade-offs and who is affected are visible at the point of decision.",
    needs: "A named owner for AI risk, not a paragraph in a policy document.",
    watchOut: "Ethics reviewed at the end is documentation, not design.",
    scope: "A risk and impact check built into the release path for AI features.",
  },
}

/**
 * Null rather than a throw: a card id is stored data, and a session written
 * against a deck that has since been re-cut must still render.
 */
export function cardFace(id: CardId): CardFace | null {
  return CARD_FACES[id] ?? null
}
